import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Dialog,
  FooterAction,
  GroupedPanelList,
  IconButton,
  Item,
  Tab,
  TabContext,
  TabList,
  TabPanel,
} from "@deere/isg.component-library";
import {
  IconChevronLeft,
  IconCircleClose,
  IconDef,
  IconDirections,
  IconEdit,
  IconFuel,
  IconMachineHours,
  IconPanelOn,
  IconAddColumn,
  IconStar,
  IconStarEmpty,
  LocationPin,
  SeparatorHours,
  StatusAutonomyActive,
  StatusAutonomyNotReady,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
  IconAnalyze,
  IconNavigationTools,
  IconChevronRight,
  IconMarkerEmpty,
  Dumptruck,
} from "@deere/icons/new";
import { useMap } from "@vis.gl/react-google-maps";

import "./EquipmentPanel.css";
import Utilization from "./tools/Utilization";
import RightNow from "./tools/RightNow";
import { ReadinessChecklist } from "./tools/RightNow";
import Tools from "./tools/Tools";
import CabControl from "./CabControl";
import TickingTime from "../../../components/TickingTime";
import { hasPermission } from "../../../../permissions";
import { getNavigatorState } from "../../../../modules/navigatorStateHelper";
import {
  getMachineDisplayState,
  isStalledByGeometryFault,
} from "../../../../modules/machineStateDisplay";
import { AppContext } from "../../../context/AppContext";
import useApiCall from "../../../hooks/useApiCall";
import { enqueueToaster } from "@deere/isg.component-library";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";

interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

interface PolylineProps {
  points: string; // A string representing the points of the polyline, e.g., "10,10 190,10 160,190 40,190"
  stroke?: string; // Optional stroke color for the line
  strokeWidth?: number; // Optional stroke width for the line
}

const Polyline: React.FC<PolylineProps> = ({
  points,
  stroke = "black", // Default stroke color
  strokeWidth = 2, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polyline
        points={points}
        fill="none" // No fill for the polyline
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
};

function getZoneIcon(zone: any, zoneId: string) {
  const zoneType = zone.zoneType || "Undefined Zone Type";

  function scaleValue(
    value: number,
    originalMin: number,
    originalMax: number,
    newMin: number,
    newMax: number,
    isLatitude: boolean = false,
  ): number {
    // Scale the value from the original range to the new range
    const scaledValue =
      ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin) + newMin;

    // If scaling latitude, invert the value
    return isLatitude ? newMax - (scaledValue - newMin) : scaledValue;
  }

  // Define the new range
  const newMin = 10;
  const newMax = 190;

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  if (zone?.points) {
    zone.points.forEach((point: any) => {
      bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
    });
  }

  // Get original bounds for scaling
  const originalLatValues: any = [];
  const originalLngValues: any = [];
  if (zone?.points) {
    zone.points.forEach((point: any) => {
      originalLatValues.push(point.location.latitude);
      originalLngValues.push(point.location.longitude);
    });
  }
  // console.log(originalLatValues);

  const latOriginalMin = Math.min(...originalLatValues);
  const latOriginalMax = Math.max(...originalLatValues);
  const lngOriginalMin = Math.min(...originalLngValues);
  const lngOriginalMax = Math.max(...originalLngValues);

  // Scale points for Polyline
  const points = zone?.points
    ? zone.points
        .map((point: any) => {
          const scaledLongitude = scaleValue(
            point.location.longitude,
            lngOriginalMin,
            lngOriginalMax,
            newMin,
            newMax,
          );
          const scaledLatitude = scaleValue(
            point.location.latitude,
            latOriginalMin,
            latOriginalMax,
            newMin,
            newMax,
            true,
          );
          return `${scaledLongitude},${scaledLatitude}`;
        })
        .join(" ")
    : "";

  // Define the stroke and fill values
  let strokeValue = "";
  let fillValue = "";
  let subtitle = "";
  if (zoneType == "FREE_SPACE_ZONE") {
    strokeValue = "#1c6521";
    fillValue = "#33bd3d";
    subtitle = "Free Space Zone";
  } else if (zoneType == "RESTRICTED_ZONE") {
    strokeValue = "#8b1717";
    fillValue = "#e42929";
    subtitle = "Restricted Zone";
  } else if (zoneType == "BOUNDARY") {
    strokeValue = "#FF00FF";
    fillValue = "#FF00FF4D";
    subtitle = "Boundary Zone";
  } else if (zoneType == "OI_OBJECT") {
    strokeValue = "rgb(0, 4, 255)";
    fillValue = "rgba(0, 4, 255, 0.3)";
    subtitle = "OI Object";
  } else if (zoneType == "WORK_ZONE") {
    strokeValue = "rgb(255, 136, 0)";
    fillValue = "rgba(255, 136, 0, 0.3)";
    subtitle = "Work Zone";
  }

  // return <Polygon points={points} stroke={strokeValue} fill={fillValue} strokeWidth={4} />;

  return {
    icon: <Polygon points={points} stroke={strokeValue} fill={fillValue} strokeWidth={4} />,
    mapbounds: bounds,
  };
}

function getLineIcon(line: any, lineId: string) {
  const lineType = line.pathType || "Undefined Line Type";

  function scaleValue(
    value: number,
    originalMin: number,
    originalMax: number,
    newMin: number,
    newMax: number,
    isLatitude: boolean = false,
  ): number {
    // Scale the value from the original range to the new range
    const scaledValue =
      ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin) + newMin;

    // If scaling latitude, invert the value
    return isLatitude ? newMax - (scaledValue - newMin) : scaledValue;
  }

  // Define the new range
  const newMin = 10;
  const newMax = 190;

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  line?.points?.forEach((point: any) => {
    bounds.extend(new google.maps.LatLng(point.location?.latitude, point.location?.longitude));
  });

  // Get original bounds for scaling
  const originalLatValues: any = [];
  const originalLngValues: any = [];
  if (line.hasOwnProperty("points")) {
    line.points.forEach((point: any) => {
      originalLatValues.push(point.location.latitude);
      originalLngValues.push(point.location.longitude);
    });
  }
  // console.log(originalLatValues);

  const latOriginalMin = Math.min(...originalLatValues);
  const latOriginalMax = Math.max(...originalLatValues);
  const lngOriginalMin = Math.min(...originalLngValues);
  const lngOriginalMax = Math.max(...originalLngValues);

  // Scale points for Polyline
  const points = line
    ? line?.points
        ?.map((point: any) => {
          const scaledLongitude = scaleValue(
            point.location.longitude,
            lngOriginalMin,
            lngOriginalMax,
            newMin,
            newMax,
          );
          const scaledLatitude = scaleValue(
            point.location.latitude,
            latOriginalMin,
            latOriginalMax,
            newMin,
            newMax,
            true,
          );
          return `${scaledLongitude},${scaledLatitude}`;
        })
        .join(" ")
    : "";

  // Define the stroke and fill values
  let strokeValue = "#3290ff";
  let subtitle = "";
  if (lineType == "NAVIGABLE_PATH") {
    strokeValue = "#0066ec";
    subtitle = "Navigable Path";
  } else if (lineType == "WINDROW") {
    strokeValue = "#7700c7";
    subtitle = "Windrow";
  } else if (lineType == "STATIC_ROUTE") {
    strokeValue = "#00FFC3";
    subtitle = "Static Route";
  }

  // return <Polyline points={points} stroke={strokeValue} strokeWidth={4} />

  return {
    icon: <Polyline points={points} stroke={strokeValue} strokeWidth={4} />,
    mapbounds: bounds,
  };
}

function getPointIcon(point: any, pointId: string) {
  const pointType = point.pointType || "Undefined Line Type";

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  bounds.extend(
    new google.maps.LatLng(point.location.latitude + 0.001, point.location.longitude + 0.001),
  );
  bounds.extend(
    new google.maps.LatLng(point.location.latitude - 0.001, point.location.longitude - 0.001),
  );

  // Define the stroke and fill values
  let subtitle = "Unkown Type";
  // DUMP_POINT = 0;
  // LOAD_POINT = 1;
  if (pointType == "DUMP_POINT") {
    subtitle = "Dump Point";
  } else if (pointType == "LOAD_POINT") {
    subtitle = "Load Point";
  }

  // return <IconMarkerEmpty primary={'#FFFFFF'} outline={'transparent'}
  //     style={{
  //         position: "relative",
  //         top: "0px",
  //         minHeight: "25px",
  //         minWidth: "25px",
  //         height: "auto",
  //         width: "auto"
  //     }}
  // />;

  return {
    icon: (
      <IconMarkerEmpty
        primary={"#FFFFFF"}
        outline={"transparent"}
        style={{
          position: "relative",
          top: "0px",
          minHeight: "25px",
          minWidth: "25px",
          height: "auto",
          width: "auto",
        }}
      />
    ),
    mapbounds: bounds,
  };
}

function getZoneItem(
  zone: any,
  zoneId: string,
): {
  id: string;
  title: string;
  subtitle: string;
  icon: JSX.Element;
  mapbounds: any;
} {
  const title = zoneId || "Undefined Zone";
  const zoneType = zone.zoneType || "Undefined Zone Type";

  function scaleValue(
    value: number,
    originalMin: number,
    originalMax: number,
    newMin: number,
    newMax: number,
    isLatitude: boolean = false,
  ): number {
    // Scale the value from the original range to the new range
    const scaledValue =
      ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin) + newMin;

    // If scaling latitude, invert the value
    return isLatitude ? newMax - (scaledValue - newMin) : scaledValue;
  }

  // Define the new range
  const newMin = 10;
  const newMax = 190;

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  if (zone?.points) {
    zone.points.forEach((point: any) => {
      bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
    });
  }

  // Get original bounds for scaling
  const originalLatValues: any = [];
  const originalLngValues: any = [];
  if (zone?.points) {
    zone.points.forEach((point: any) => {
      originalLatValues.push(point.location.latitude);
      originalLngValues.push(point.location.longitude);
    });
  }
  // console.log(originalLatValues);

  const latOriginalMin = Math.min(...originalLatValues);
  const latOriginalMax = Math.max(...originalLatValues);
  const lngOriginalMin = Math.min(...originalLngValues);
  const lngOriginalMax = Math.max(...originalLngValues);

  // Scale points for Polyline
  const points = zone?.points
    ? zone.points
        .map((point: any) => {
          const scaledLongitude = scaleValue(
            point.location.longitude,
            lngOriginalMin,
            lngOriginalMax,
            newMin,
            newMax,
          );
          const scaledLatitude = scaleValue(
            point.location.latitude,
            latOriginalMin,
            latOriginalMax,
            newMin,
            newMax,
            true,
          );
          return `${scaledLongitude},${scaledLatitude}`;
        })
        .join(" ")
    : "";

  // Define the stroke and fill values
  let strokeValue = "";
  let fillValue = "";
  let subtitle = "";
  if (zoneType == "FREE_SPACE_ZONE") {
    strokeValue = "#1c6521";
    fillValue = "#33bd3d";
    subtitle = "Free Space Zone";
  } else if (zoneType == "RESTRICTED_ZONE") {
    strokeValue = "#8b1717";
    fillValue = "#e42929";
    subtitle = "Restricted Zone";
  } else if (zoneType == "BOUNDARY") {
    strokeValue = "#FF00FF";
    fillValue = "#FF00FF4D";
    subtitle = "Boundary Zone";
  } else if (zoneType == "OI_OBJECT") {
    strokeValue = "rgb(0, 4, 255)";
    fillValue = "rgba(0, 4, 255, 0.3)";
    subtitle = "OI Object";
  } else if (zoneType == "WORK_ZONE") {
    strokeValue = "rgb(255, 136, 0)";
    fillValue = "rgba(255, 136, 0, 0.3)";
    subtitle = "Work Zone";
  }

  // Create the mission item object
  const missionItem = {
    id: zoneId,
    title: title,
    subtitle: subtitle,
    icon: <Polygon points={points} stroke={strokeValue} fill={fillValue} strokeWidth={4} />, // Use points to draw the Polyline
    mapbounds: bounds,
  };

  // console.log(points);

  return missionItem;
}

function getLineItem(
  line: any,
  lineId: string,
): {
  id: string;
  title: string;
  subtitle: string;
  icon: JSX.Element;
  mapbounds: any;
} {
  const title = lineId || "Undefined Line";
  const lineType = line.pathType || "Undefined Line Type";

  function scaleValue(
    value: number,
    originalMin: number,
    originalMax: number,
    newMin: number,
    newMax: number,
    isLatitude: boolean = false,
  ): number {
    // Scale the value from the original range to the new range
    const scaledValue =
      ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin) + newMin;

    // If scaling latitude, invert the value
    return isLatitude ? newMax - (scaledValue - newMin) : scaledValue;
  }

  // Define the new range
  const newMin = 10;
  const newMax = 190;

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  line?.points?.forEach((point: any) => {
    bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
  });

  // Get original bounds for scaling
  const originalLatValues: any = [];
  const originalLngValues: any = [];
  if (line.hasOwnProperty("points")) {
    line.points.forEach((point: any) => {
      originalLatValues.push(point.location.latitude);
      originalLngValues.push(point.location.longitude);
    });
  }
  // console.log(originalLatValues);

  const latOriginalMin = Math.min(...originalLatValues);
  const latOriginalMax = Math.max(...originalLatValues);
  const lngOriginalMin = Math.min(...originalLngValues);
  const lngOriginalMax = Math.max(...originalLngValues);

  // Scale points for Polyline
  const points = line
    ? line?.points
        ?.map((point: any) => {
          const scaledLongitude = scaleValue(
            point.location.longitude,
            lngOriginalMin,
            lngOriginalMax,
            newMin,
            newMax,
          );
          const scaledLatitude = scaleValue(
            point.location.latitude,
            latOriginalMin,
            latOriginalMax,
            newMin,
            newMax,
            true,
          );
          return `${scaledLongitude},${scaledLatitude}`;
        })
        .join(" ")
    : "";

  // Define the stroke and fill values
  let strokeValue = "#3290ff";
  let subtitle = "";
  if (lineType == "NAVIGABLE_PATH") {
    strokeValue = "#0066ec";
    subtitle = "Navigable Path";
  } else if (lineType == "WINDROW") {
    strokeValue = "#7700c7";
    subtitle = "Windrow";
  } else if (lineType == "STATIC_ROUTE") {
    strokeValue = "#00FFC3";
    subtitle = "Static Route";
  }

  // Create the mission item object
  const missionItem = {
    id: lineId,
    title: title,
    subtitle: subtitle,
    icon: <Polyline points={points} stroke={strokeValue} strokeWidth={4} />, // Use points to draw the Polyline
    mapbounds: bounds,
  };

  // console.log(points);

  return missionItem;
}

function getPointItem(
  point: any,
  pointId: string,
): {
  id: string;
  title: string;
  subtitle: string;
  icon: JSX.Element;
  mapbounds: any;
} {
  const title = pointId || "Undefined Line";
  const pointType = point.pointType || "Undefined Line Type";

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  bounds.extend(
    new google.maps.LatLng(point.location.latitude + 0.001, point.location.longitude + 0.001),
  );
  bounds.extend(
    new google.maps.LatLng(point.location.latitude - 0.001, point.location.longitude - 0.001),
  );

  // Define the stroke and fill values
  let subtitle = "Unkown Type";
  // DUMP_POINT = 0;
  // LOAD_POINT = 1;
  if (pointType == "DUMP_POINT") {
    subtitle = "Dump Point";
  } else if (pointType == "LOAD_POINT") {
    subtitle = "Load Point";
  }

  // Create the mission item object
  const pointItem = {
    id: pointId,
    title: title,
    subtitle: subtitle,
    icon: (
      <IconMarkerEmpty
        primary={"#FFFFFF"}
        outline={"transparent"}
        style={{
          position: "relative",
          top: "0px",
          minHeight: "25px",
          minWidth: "25px",
          height: "auto",
          width: "auto",
        }}
      />
    ), // Use points to draw the Polyline
    mapbounds: bounds,
  };

  // console.log(points);
  return pointItem;
}

function getStringTime(unixTime: number) {
  let formattedTime = "";
  if (unixTime) {
    const elapsed = Math.floor(unixTime / 1000); // Get elapsed time in seconds
    const days = Math.floor(elapsed / (3600 * 24));
    const hours = Math.floor((elapsed % (3600 * 24)) / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (days > 0) {
      formattedTime = `${days} days ${hours} hr`;
    } else if (hours > 0) {
      formattedTime = `${hours} hr ${minutes} min`;
    } else if (minutes > 0) {
      formattedTime = `${minutes} min ${seconds} sec`;
    } else {
      formattedTime = `${seconds} sec`;
    }
  } else {
    // formattedTime = `0 sec`;
    // console.log("Error, unix time unknown: ", unixTime);
    formattedTime = `0 sec`;
  }
  return formattedTime;
}

function EquipmentPanel({
  toggleCabControl,
  showCabControl,
  orgDataSession,
  userLocation,
  setUserLocation,
  isSpoofing,
  setIsSpoofing,
  originalLocation,
  setOriginalLocation,
  orgData,
  currentTopicString,
  hideSideBar,
  curEquipment,
  handleClose,
  changeFirstPanel,
  panelMode,
  togglePanelMode,
  followedVehicleId,
  setFollowedVehicleId,
  onNavigateToMissions,
  onNavigateToAttributes,
  missionPreviewActive,
  setMissionPreviewActive,
  missionPreviewPaths,
  setMissionPreviewPaths,
  missionPreviewPoints,
  setMissionPreviewPoints,
  orgName,
}: {
  toggleCabControl: (vehicleName?: string) => void;
  showCabControl: boolean;
  orgDataSession: any;
  userLocation: any;
  setUserLocation: (loc: any) => void;
  isSpoofing: boolean;
  setIsSpoofing: (val: boolean) => void;
  originalLocation: any;
  setOriginalLocation: (loc: any) => void;
  orgData: any;
  currentTopicString: any;
  hideSideBar: () => void;
  curEquipment: Item | undefined;
  handleClose: () => void;
  changeFirstPanel: () => void;
  panelMode: "one" | "two";
  togglePanelMode: () => void;
  followedVehicleId?: string | null;
  setFollowedVehicleId?: (id: string | null) => void;
  onNavigateToMissions?: () => void;
  onNavigateToAttributes?: () => void;
  missionPreviewActive?: boolean;
  setMissionPreviewActive?: (active: boolean) => void;
  missionPreviewPaths?: any[];
  setMissionPreviewPaths?: (paths: any[]) => void;
  missionPreviewPoints?: any[];
  setMissionPreviewPoints?: (points: any[]) => void;
  orgName?: string;
}) {
  const { user } = React.useContext(AppContext);
  const role = user.role;

  const [equip, setEquip] = useState<Item>();
  const [serialCode, setSerialCode] = useState("1DW406EBALF705342");
  const [value, setValue] = useState("1");
  const [isFavorite, setIsFavorite] = useState(false);
  const [autonomyStatus, setAutonomyStatus] = useState("Off");
  const [navigatorStatus, setNavigatorStatus] = useState("Off");
  const [navigatorTransition, setNavigatorTransition] = useState("ready");
  const [timeSince, setTimeSince] = useState("As of now");
  const [equipObject, setEquipObject] = useState({});
  // const [followVehicle, setFollowVehicle] = useState(false);
  const [alertArrayState, setAlertArrayState] = useState<Array<any>>();
  const [updateCounter, setUpdateCounter] = useState(0);
  const [engineState, setEngineState] = useState<string>("Off");
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [isWithinRange, setIsWithinRange] = useState<boolean>(false);
  const [canGiveCommand, setCanGiveCommand] = useState<boolean>(false);
  const [navigatorTransitionLabel, setNavigatorTransitionLabel] = useState("");
  const [jobsiteAutonomyState, setJobsiteAutonomyState] = useState<string>("off");
  const [jobsiteAutonomyStateTimestamp, setJobsiteAutonomyStateTimestamp] = useState<number>(0);
  const [equipmentName, setEquipmentName] = useState<string>("");
  const [equipmentModel, setEquipmentModel] = useState<string>("");
  const [showCabControlPopUp, setShowCabControlPopUp] = useState(false);

  const { execute } = useApiCall({ errorAsToast: true });
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  // if (alertArray) {
  //     alertArray = [alertArray.pop()];
  // }

  // function addAlert(thisAlert: string) {
  //     console.log("I've been called!!!");
  //     setAlert(<></>);
  //     setAlert(thisAlert);
  // }

  // useEffect(() => {
  //     console.log("Change in the alertArray");
  //     setAlertArrayState(alertArray);
  // }, [alertArray]);

  const map = useMap("mainMap"); // Get the map instance

  function findVehicle(orgDataSession: any, curEquipment: any): any | undefined {
    // Split the curEquipment.id by '/' to get the keys
    const keys = curEquipment.id.replace(/_/g, "/").split("/");

    const findSpecificVehicle = (obj: any): object | null => {
      // Check if the current object has a Missions key
      if (obj && typeof obj === "object") {
        if (keys[keys.length - 1] in obj) {
          let returnObj = obj[keys[keys.length - 1]];
          return {
            ...returnObj,
            topicPortion: curEquipment.id,
          }; // Return the Missions object if found
        }

        // Recursively search through each property in the object
        for (const key in obj) {
          const result = findSpecificVehicle(obj[key]);
          if (result) {
            return result; // Return the result if Missions is found in the current branch
          }
        }
      }
      return null; // Return null if Missions is not found
    };
    const vehicle = findSpecificVehicle(orgDataSession);
    return vehicle ? vehicle : {}; // Return found missions or an empty object

    // // Function to recursively search through the orgData
    // const search = (data: any, keyIndex: number): any | undefined => {
    //     // Check if we've processed all keys
    //     if (keyIndex >= keys.length) {
    //         return data; // Return the found object if all keys have been processed
    //     }

    //     const currentKey = keys[keyIndex];

    //     // Loop through each property in the current data object
    //     for (const key in data) {
    //         if (data.hasOwnProperty(key) && key === currentKey) {
    //             // If the current key matches, continue searching in its children
    //             return search(data[key], keyIndex + 1);
    //         }
    //     }

    //     // If no match is found in the current level
    //     return undefined;
    // };

    // // Start the search from the orgData
    // return search(orgData, 0);
  }

  useEffect(() => {
    // // Navigator NavigatorState state
    // let currentState = (equipObject as any)?.Navigator?.NavigatorState?.state;
    // // console.log(currentState);
    // if (currentState === "GENERATING_PATH") {
    //     setAutonomyStatus("activeIdle");
    // } else if (currentState === "READY") {
    //     setAutonomyStatus("ready");
    // } else if (currentState === "ENGAGED") {
    //     setAutonomyStatus("activeWorking");
    // } else if (currentState === "INTERRUPTED") {
    //     setAutonomyStatus("activeIdle");
    // } else if (currentState === "OFF") {
    //     setAutonomyStatus("ready");
    // } else {
    //     // console.log("Autonomy Status Unknown");
    //     setAutonomyStatus("off");
    // }

    let currentState = Number((equipObject as any)?.VADC?.autonomy?.state?.[0]);
    // console.log("Current State: " + currentState);
    // console.log("Current State Off: " + (currentState == 0));
    // console.log("Current State STANDBY: " + (currentState == 1));
    // console.log("Current State ACTIVE: " + (currentState == 2));
    // console.log("Current State LOW_ENERGY_STATE: " + (currentState == 3));
    if (currentState == 0) {
      // Autonomy State: OFF
      setAutonomyStatus("OFF");
    } else if (currentState == 1) {
      // Autonomy State: STANDBY
      setAutonomyStatus("STANDBY");
    } else if (currentState == 2) {
      // ACTIVE, but a navigator geometry fault means it can never actually run,
      // so report STANDBY rather than showing the operator autonomy is active.
      if (isStalledByGeometryFault(equipObject)) {
        setAutonomyStatus("STANDBY");
      } else {
        setAutonomyStatus("ACTIVE");
      }
    } else if (currentState == 3) {
      // Autonomy State: LOW_ENERGY_STATE
      setAutonomyStatus("LOW_ENERGY_STATE");
    } else if (currentState == 255) {
      // Autonomy State: INVALID
      setAutonomyStatus("INVALID");
    } else {
      // Autonomy State: Unknown by Orchestrator
      setAutonomyStatus("Unknown");
    }

    // Navigator State
    let navigatorState = getNavigatorState((equipObject as any)?.Navigator?.NavigatorState);
    // console.log("navigatorState: " + (equipObject as any)?.Navigator?.NavigatorState?.state);

    if (navigatorState == "OFF") {
      // Autonomy State: OFF
      setNavigatorStatus("OFF");
    } else if (navigatorState == "GENERATING_PATH") {
      // Autonomy State: STANDBY
      setNavigatorStatus("GENERATING_PATH");
    } else if (navigatorState == "READY") {
      // Autonomy State: ACTIVE
      setNavigatorStatus("READY");
    } else if (navigatorState == "ENGAGED") {
      // Autonomy State: LOW_ENERGY_STATE
      setNavigatorStatus("ENGAGED");
    } else if (navigatorState == "INTERRUPTED") {
      // Autonomy State: INVALID
      setNavigatorStatus("INTERRUPTED");
    } else {
      // Autonomy State: Unknown by Orchestrator
      setNavigatorStatus("OFF");
    }

    // Checking for messages from ADT
    // Navigator Transition Label
    // ready
    // path finished
    setNavigatorTransitionLabel(
      (equipObject as any)?.Navigator?.NavigatorState?.transitionLabel as string,
    );
    // console.log("Trans Label:" + navigatorTransitionLabel + ":");
    // console.log(navigatorTransitionLabel);

    // const [equipmentName, setEquipmentName] = useState<string>('');
    // const [equipmentModel, setEquipmentModel] = useState<string>('');
    // console.log("curEquipment", curEquipment);
    let nameArray: string[] = curEquipment
      ? (curEquipment.id as any).replace(/_/g, "/").split("/")
      : ["", "", ""];
    setEquipmentName(nameArray[0] + " " + nameArray[2]);
    setEquipmentModel(nameArray[1]);
    // console.log("nameArray[0] + nameArray[2]", nameArray[0] + nameArray[2]);
    // console.log("nameArray[1]",nameArray[1]);
  }, [equipObject]);

  // Show a toast notification when navigator transition label changes
  useEffect(() => {
    if (navigatorTransitionLabel) {
      enqueueToaster(navigatorTransitionLabel, "Navigator", {
        variant: "info",
        autoHideDuration: 7000,
      });
    }
  }, [navigatorTransitionLabel]);

  // Derive engine state from autonomy status
  useEffect(() => {
    if (
      autonomyStatus === "ACTIVE" ||
      autonomyStatus === "STANDBY" ||
      autonomyStatus === "LOW_ENERGY_STATE"
    ) {
      setEngineState("On");
    } else {
      setEngineState("Off");
    }
  }, [autonomyStatus]);

  // Check for machine errors
  useEffect(() => {
    try {
      setHasErrors(
        (equipObject as any)?.errors?.errorsList &&
          (equipObject as any)?.errors?.errorsList?.length > 0,
      );
    } catch (error) {}
  }, [equipObject]);

  // Haversine distance helper (returns meters)
  const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3;
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  // Check if user is within range of equipment (3000 feet)
  function checkIfInRange() {
    const equipLat =
      (equipObject as any)?.front?.axle?.center?.GlobalPose?.location?.latitude ??
      (equipObject as any)?.MachineState?.GlobalPose?.location?.latitude;
    const equipLon =
      (equipObject as any)?.front?.axle?.center?.GlobalPose?.location?.longitude ??
      (equipObject as any)?.MachineState?.GlobalPose?.location?.longitude;
    const userLat = userLocation?.latitude;
    const userLon = userLocation?.longitude;

    if (
      equipLat !== undefined &&
      equipLon !== undefined &&
      userLat !== undefined &&
      userLon !== undefined
    ) {
      const distanceFeet = haversineDistance(equipLat, equipLon, userLat, userLon) * 3.28084;
      setIsWithinRange(distanceFeet < 3000);
    } else {
      setIsWithinRange(false);
    }
  }

  useEffect(() => {
    checkIfInRange();
  }, [equipObject, userLocation]);


  /*
    useEffect(() => {
        if (followVehicle) {
            let thisLat = null;
            let thisLng = null;
            let equipment: any = equipObject;
            try {
                // Check if the different location formats on the vehicles
                if (equipment.MachineState !== undefined && equipment.MachineState.GlobalPose !== undefined) {
                    thisLat = equipment.MachineState.GlobalPose.location.latitude;
                    thisLng = equipment.MachineState.GlobalPose.location.longitude;
                } else if (equipment.front_axle_center.GlobalPose !== undefined) {
                    thisLat = equipment.front_axle_center.GlobalPose.location.latitude;
                    thisLng = equipment.front_axle_center.GlobalPose.location.longitude;
                }
            } catch (error) { console.log(error) }

            // Create bounds for map selection
            const vehicleZoomBounds = new google.maps.LatLngBounds();
            if ((thisLat !== null) && (thisLng !== null)) {
                vehicleZoomBounds.extend(new google.maps.LatLng(thisLat + 0.0005, thisLng + 0.0005));
                vehicleZoomBounds.extend(new google.maps.LatLng(thisLat - 0.0005, thisLng - 0.0005));
            }
            map?.fitBounds(vehicleZoomBounds);
        }
    }, [orgDataSession]);
*/

  useEffect(() => {
    // try {

    //     console.log("curEquipment", curEquipment);
    //     console.log(findVehicle(orgData,curEquipment));

    // } catch (error) {

    // }
    const freshEquipObject = findVehicle(orgDataSession, curEquipment);
    setEquipObject(freshEquipObject);

    // Derive the display state from LIVE VADC + Navigator.
    // autonomyLog lags behind the live state
    const { displayState } = getMachineDisplayState(freshEquipObject);
    const autonomyLog = (freshEquipObject as any)?.autonomyLog;
    const lastLogEntry = autonomyLog?.[Object.keys(autonomyLog || {}).length - 1];
    const lastTransitionTimestamp = lastLogEntry?.timestamp as number | undefined;

    setJobsiteAutonomyState(displayState);
    setJobsiteAutonomyStateTimestamp(lastTransitionTimestamp ?? 0);
  }, [curEquipment, orgDataSession]);

  useLayoutEffect(() => {
    setEquip(curEquipment);
    setSerialCode(serialCode);
  }, [curEquipment, equip, serialCode]);

  const handleChange = (event: any, newValue: React.SetStateAction<string>): void => {
    setValue(newValue);
  };

  function handleBack() {
    handleClose();
  }

  function handleShowHideList() {
    togglePanelMode();
  }

  function toggleFollowVehicle() {
    if (!curEquipment || !curEquipment.id || !setFollowedVehicleId) return;

    // Extract vehicle name from id (session_model_vehicleName)
    const parts = curEquipment.id.split("_");
    const vehicleName = parts[parts.length - 1];

    if (followedVehicleId === vehicleName) {
      setFollowedVehicleId(null);
    } else {
      setFollowedVehicleId(vehicleName);
    }
  }

  function toggleCabControlPopUp() {
    setShowCabControlPopUp(true);
  }

  const footerActions: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      onClick: (): void => {
        setShowCabControlPopUp(false);
      },
    },
    {
      title: "Confirm",
      variant: "primary",
      onClick: async (): Promise<void> => {
        // Extract vehicle name to auto-follow when entering cab control
        const parts = curEquipment?.id?.split("_");
        const vehicleName = parts ? parts[parts.length - 1] : undefined;
        toggleCabControl(vehicleName);
        setShowCabControlPopUp(false);
      },
    },
  ];

  const [cabControlMachineIcon, setCabControlMachineIcon] = useState(<></>);

  useEffect(() => {
    setCabControlMachineIcon(
      <>
        <div style={{ width: "50px", height: "50px", marginRight: "8px" }}>
          {equip && (equip.icon as ReactNode)}
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
          <b style={{ height: "16px" }}>
            {/* {equip.vehicleType + ' ' + equip.vehicleName} */}
            {equipmentName}
          </b>
          <div style={{ height: "18px" }}>
            {/* {equip.vehicleModel} */}
            {equipmentModel}
          </div>
          <div style={{ display: "flex" }}>
            {jobsiteAutonomyState == "off" && (
              <div
                style={{
                  width: "68px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  marginRight: "-23px",
                }}
              >
                <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
                Off
              </div>
            )}
            {jobsiteAutonomyState == "ready" && (
              <div
                style={{
                  width: "60px",
                  color: "#00B600",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <StatusAutonomyReady size={20} />
                Ready
              </div>
            )}
            {jobsiteAutonomyState == "paused" && (
              <div
                style={{
                  color: "rgb(255, 120, 0)",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
                Paused
              </div>
            )}
            {jobsiteAutonomyState == "working" && (
              <div
                style={{
                  color: "#00B600",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
                Working
              </div>
            )}
            <div style={{ marginLeft: "6px", color: "#767676" }}>
              <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
            </div>
          </div>
        </div>
      </>,
    );
  }, [equip, equipmentName, equipmentModel, jobsiteAutonomyState, jobsiteAutonomyStateTimestamp]);


  const cabControlOverlay = (
    <CabControl
      equip={equip}
      equipmentName={equipmentName}
      equipmentModel={equipmentModel}
      jobsiteAutonomyState={jobsiteAutonomyState}
      jobsiteAutonomyStateTimestamp={jobsiteAutonomyStateTimestamp}
      cabControlMachineIcon={cabControlMachineIcon}
      equipObject={equipObject}
      currentTopicString={currentTopicString}
      onClose={toggleCabControl}
    />
  );

  return (
    <>
      <div className="EquipmentPanel">
          <div className="TopPanel">
            <div className="TopTitleBar">
              <div className="TitleBarLeft">
                <IconButton
                  icon={
                    <span>
                      <IconChevronLeft size={24} />
                    </span>
                  }
                  variant="borderless"
                  onClick={handleBack}
                  style={{ width: "32px", height: "32px", alignSelf: "center" }}
                />
                <div className="EquipTag">
                  <div style={{ width: "50px", height: "50px", marginRight: "8px" }}>
                    {equip && (equip.icon as ReactNode)}
                  </div>
                  {/* <b>
                                    {equip ? equip.title : "Example"}
                                </b> */}
                  {/* <div>
                                {serialCode}
                            </div> */}

                  <div
                    style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}
                  >
                    <b style={{ height: "16px" }}>
                      {/* {equip.vehicleType + ' ' + equip.vehicleName} */}
                      {equipmentName}
                    </b>
                    <div style={{ height: "18px" }}>
                      {/* {equip.vehicleModel} */}
                      {equipmentModel}
                    </div>
                    <div style={{ display: "flex" }}>
                      {jobsiteAutonomyState == "off" && (
                        <div
                          style={{
                            width: "68px",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            marginRight: "-23px",
                          }}
                        >
                          <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
                          Off
                        </div>
                      )}
                      {jobsiteAutonomyState == "ready" && (
                        <div
                          style={{
                            width: "60px",
                            color: "#00B600",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <StatusAutonomyReady size={20} />
                          Ready
                        </div>
                      )}
                      {jobsiteAutonomyState == "paused" && (
                        <div
                          style={{
                            color: "rgb(255, 120, 0)",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                          <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
                          Paused
                        </div>
                      )}
                      {jobsiteAutonomyState == "working" && (
                        <div
                          style={{
                            color: "#00B600",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                          <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
                          Working
                        </div>
                      )}
                      <div style={{ marginLeft: "6px", color: "#767676" }}>
                        <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
                      </div>
                    </div>
                  </div>

                  {/* <div className='EquipText'>
                                <b>
                                    {equip ? equip.title : "h"}
                                </b>
                                <div>
                                    {serialCode}
                                </div>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {(autonomyStatus !== "STANDBY" && autonomyStatus !== "ACTIVE") &&
                                        <div style={{ width: "68px", fontWeight: "bold", display: "flex", alignItems: "center", marginRight: "-23px" }}>
                                            <StatusAutonomyOff size={20} sx={{ marginRight: '4px' }} />
                                            Off
                                        </div>

                                    }
                                    {autonomyStatus == "STANDBY" &&
                                        <div style={{ width: "68px", color: "#00B600", fontWeight: "bold", display: "flex", alignItems: "center" }}>
                                            <StatusAutonomyReady size={20} />
                                            Ready
                                        </div>
                                    }{(autonomyStatus == "ACTIVE" && navigatorStatus == "INTERRUPTED") &&
                                        <div style={{ color: "rgb(255, 120, 0)", fontWeight: "bold", display: "flex", alignItems: "center" }}>
                                            <StatusAutonomyActive size={20} sx={{ marginRight: '-10px' }} />
                                            <StatusEquipmentIdle size={20} sx={{ marginRight: '4px' }} />
                                            Paused
                                        </div>
                                    }
                                    {(autonomyStatus == "ACTIVE" && navigatorStatus !== "INTERRUPTED") &&
                                        <div style={{ color: "#00B600", fontWeight: "bold", display: "flex", alignItems: "center" }}>
                                            <StatusAutonomyActive size={20} sx={{ marginRight: '-10px' }} />
                                            <StatusEquipmentWorking size={20} sx={{ marginRight: '4px' }} />
                                            Working
                                        </div>}
                                    <div style={{ marginLeft: "6px", color: "#767676" }}>{timeSince}</div>
                                </div>
                            </div> */}
                </div>
              </div>
              <div className="exitButtons">
                <IconButton
                  style={{ pointerEvents: "auto" }}
                  icon={
                    <span>
                      {panelMode === "one" ? <IconAddColumn size={20} /> : <IconPanelOn size={20} />}
                    </span>
                  }
                  variant="borderless"
                  onClick={handleShowHideList}
                />
                <IconButton
                  icon={
                    <span>
                      <IconCircleClose size={20} />
                    </span>
                  }
                  variant="borderless"
                  onClick={handleBack}
                />
              </div>
            </div>
            <div className="StatsPanel" style={{ marginTop: "8px" }}>
              <div
                style={{
                  marginLeft: "6px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <IconMachineHours size={16} sx={{ marginRight: "2px" }} />
                -- hrs
              </div>
              <div
                style={{
                  marginLeft: "18px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <SeparatorHours size={16} sx={{ marginRight: "4px" }} />
                -- hrs
              </div>
              <div
                style={{
                  marginLeft: "18px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <IconFuel size={16} sx={{ marginRight: "4px" }} />
                -- %
              </div>
              <div
                style={{
                  marginLeft: "18px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <IconDef size={16} sx={{ marginRight: "4px" }} />
                -- %
              </div>
            </div>
            <div className="ListManagement">
              <IconButton
                className="starButton"
                icon={isFavorite ? <IconStar /> : <IconStarEmpty />}
                style={{ width: "40px", height: "40px", marginLeft: "-4px" }}
                variant="borderless"
                onClick={() => setIsFavorite(!isFavorite)}
              />

              <IconButton
                className="editButton"
                icon={<IconEdit size={20} />}
                variant="borderless"
                style={{ marginRight: "4px" }}
              />

              <u>Manage Tags</u>
            </div>
            <div className="ButtonPanel">
              {/* {autonomyStatus === "activeWorking" &&
                        <Button
                            startIcon={<IconVideoPlay primary={{ style: { fill: "#367C2B" } }} icon={{ style: { width: "20px", height: "20px" } }} />}
                            style={{ border: "1px solid var(--Text-Interactive, #367C2B)", color: "#367C2B", marginLeft: "6px", marginRight: "6px" }}
                        >
                            Resume
                        </Button>}

                    {autonomyStatus === "activeWorking" &&
                        <Button
                            startIcon={<IconCircleArrowRight icon={{ style: { width: "20px", height: "20px" } }} />}
                            style={{ border: "1px solid rgba(51, 51, 51, 0.20)", marginLeft: "6px", marginRight: "6px" }}
                        >
                            Go To
                        </Button>} */}

              {/* <Button
                        startIcon={<IconDirections size={20} />}
                        style={{ border: "1px solid rgba(51, 51, 51, 0.20)", marginLeft: "6px", marginRight: "6px" }}
                    >
                        Directions
                    </Button> */}
              {(hasPermission(role, "COME_TO_LOAD_POINT") ||
                hasPermission(role, "READY_TO_LOAD") ||
                hasPermission(role, "FINISHED_LOADING")) && (
                <Button
                  startIcon={<IconNavigationTools size={20} />}
                  variant="primary"
                  onClick={toggleCabControlPopUp}
                  style={{
                    border: "1px solid rgba(51, 51, 51, 0.20)",
                    marginLeft: "6px",
                    marginRight: "6px",
                  }}
                >
                  Cab Control
                </Button>
              )}

              {hasPermission(role, "START_AUTONOMY") && <></>}

              {(followedVehicleId === curEquipment?.id?.split("_").pop() && (
                <Button
                  startIcon={<LocationPin size={20} />}
                  onClick={toggleFollowVehicle}
                  style={{
                    border: "1px solid rgba(51, 51, 51, 0.20)",
                    marginLeft: "6px",
                    marginRight: "6px",
                  }}
                >
                  Un-Follow
                </Button>
              )) || (
                <Button
                  startIcon={<LocationPin size={20} />}
                  onClick={toggleFollowVehicle}
                  style={{
                    border: "1px solid rgba(51, 51, 51, 0.20)",
                    marginLeft: "6px",
                    marginRight: "6px",
                  }}
                >
                  Follow
                </Button>
              )}

              {/* <Button
                        startIcon={<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10.2658 11.721H11.5717V15.1083H1.5V3H9.02238L11.5722 5.64856V8.60338H10.2663V6.1802H8.43732V4.29782H2.79731V13.8115H10.2663L10.2658 11.721ZM3.60775 6.06319V5.28836H7.39184V6.06319H3.60775ZM3.60775 8.27981V7.49583H9.49044V8.27981H3.60775ZM3.60775 10.5407V9.76587H8.06746V10.5407H3.60775ZM3.60775 12.739V11.955H9.49044V12.739H3.60775ZM16.5 10.1627L12.9321 13.3246V11.2346H8.66168V9.07245H12.9321V6.97334L16.5 10.1627Z" fill="#333333" />
                        </svg>}

                        endIcon={<IconChevronDown icon={{ style: { width: "15px", height: "15px" } }} />}

                        style={{ border: "1px solid rgba(51, 51, 51, 0.20)", marginLeft: "6px", marginRight: "6px" }}
                    >
                        Share/Export
                    </Button> */}
            </div>
          </div>
          <TabContext value={value}>
            <TabList onChange={handleChange} variant="scrollable" scrollButtons={false}>
              <Tab label="Right Now" value="1" />
              <Tab label="Summary" value="2" />
              {hasPermission(role, "ORCHESTRATOR_ENG_TOOLS") && <Tab label="Tools" value="3" />}
              {/* <Tab label="Alerts" value="3" />
                    <Tab label="Maintenance" value="4" />
                    <Tab label="Setup" value="5" /> */}
            </TabList>
            <TabPanel value="1" sx={{ padding: "16px 0 0 0" }}>
              <div className="subpanelDiv">
                <RightNow
                  navigatorStatus={navigatorStatus}
                  userLocation={userLocation}
                  equipObject={equipObject}
                  autonomyStatus={autonomyStatus}
                  orgDataSession={orgDataSession}
                  orgData={orgData}
                  currentTopicString={currentTopicString}
                  curEquipment={curEquipment}
                  onNavigateToMissions={onNavigateToMissions}
                  onNavigateToAttributes={onNavigateToAttributes}
                />
              </div>
            </TabPanel>
            <TabPanel value="2" sx={{ padding: "16px 0 0 0" }}>
              <div className="subpanelDiv">
                <Utilization equipObject={equipObject} jobsiteIdentifier={currentTopicString} />
              </div>
            </TabPanel>
            {hasPermission(role, "ORCHESTRATOR_ENG_TOOLS") && (
              <TabPanel value="3" sx={{ padding: "16px 0 0 0" }}>
                <div className="subpanelDiv">
                  <Tools
                    navigatorStatus={navigatorStatus}
                    userLocation={userLocation}
                    setUserLocation={setUserLocation}
                    isSpoofing={isSpoofing}
                    setIsSpoofing={setIsSpoofing}
                    originalLocation={originalLocation}
                    setOriginalLocation={setOriginalLocation}
                    equipObject={equipObject}
                    autonomyStatus={autonomyStatus}
                    orgDataSession={orgDataSession}
                    orgData={orgData}
                    currentTopicString={currentTopicString}
                    curEquipment={curEquipment}
                    orgName={orgName}
                  />
                </div>
              </TabPanel>
            )}
          </TabContext>

          {/* This is for confirming the activation of autonomy */}
          <Dialog
            footerActions={footerActions}
            closeHandler={() => setShowCabControlPopUp(false)}
            open={showCabControlPopUp}
            title={"Warning"}
            width="sm"
          >
            <div
              style={{
                backgroundColor: "orange",
                width: "100%",
                padding: "16px",
                color: "black",
                borderRadius: "4px",
                border: "black 2px solid",
              }}
            >
              You are about to enter Cab Control mode to remotely operate this autonomous system.{" "}
              <br />
              <br />
              By proceeding, you confirm that you are:
              <br />
              - inside the cab
              <br />
              - have full visibility of the vehicle and its surroundings
              <br />
              - acknowledge all required safety measures
              <br />
              <br />
              <b>Continue only if conditions are safe.</b>
            </div>
          </Dialog>
        </div>

      {showCabControl === true ? createPortal(cabControlOverlay, document.body) : undefined}
    </>
  );
}

export default EquipmentPanel;
